import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Link, type LinkProps } from "react-router";
import { cn } from "@/lib/cn";
import { Spinner } from "./Feedback";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "subtle" | "white" | "link";
export type ButtonSize = "xs" | "sm" | "md" | "lg";

const base =
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-medium select-none transition-[background,border-color,color,box-shadow,transform] duration-150 active:translate-y-px disabled:opacity-45 disabled:active:translate-y-0";

const variants: Record<ButtonVariant, string> = {
  primary: "bg-primary text-white hover:bg-primary-hover active:bg-primary-press shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]",
  secondary: "border border-line-strong bg-transparent text-fg hover:bg-hover hover:border-[#4a4a50]",
  ghost: "text-muted hover:text-fg hover:bg-hover",
  danger: "bg-danger text-white hover:brightness-110",
  subtle: "bg-elevated text-fg hover:bg-elevated-2",
  white: "bg-white text-black hover:bg-zinc-200",
  link: "text-link hover:text-lavender px-0! h-auto! active:translate-y-0",
};

const sizes: Record<ButtonSize, string> = {
  xs: "h-7 px-2.5 text-xs rounded-md",
  sm: "h-8 px-3 text-[13px] rounded-lg",
  md: "h-9 px-3.5 text-[13.5px] rounded-lg",
  lg: "h-11 px-5 text-[15px] rounded-xl",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
  iconRight?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "sm", loading, icon, iconRight, className, children, disabled, type = "button", ...rest },
  ref,
) {
  return (
    <button ref={ref} type={type} className={cn(base, variants[variant], sizes[size], className)} disabled={disabled || loading} {...rest}>
      {loading ? <Spinner size={14} /> : icon}
      {children}
      {iconRight}
    </button>
  );
});

export function ButtonLink({
  variant = "secondary",
  size = "sm",
  icon,
  iconRight,
  className,
  children,
  ...rest
}: LinkProps & { variant?: ButtonVariant; size?: ButtonSize; icon?: ReactNode; iconRight?: ReactNode }) {
  return (
    <Link className={cn(base, variants[variant], sizes[size], className)} {...rest}>
      {icon}
      {children}
      {iconRight}
    </Link>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  size?: "xs" | "sm" | "md";
  active?: boolean;
  variant?: "ghost" | "outline" | "solid";
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, size = "sm", active, variant = "ghost", className, children, type = "button", ...rest },
  ref,
) {
  const dims = size === "xs" ? "size-6 rounded-md" : size === "sm" ? "size-8 rounded-lg" : "size-9 rounded-lg";
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        "inline-flex shrink-0 items-center justify-center text-muted transition-colors hover:text-fg disabled:opacity-40",
        dims,
        variant === "ghost" && "hover:bg-hover",
        variant === "outline" && "border border-line-strong hover:bg-hover",
        variant === "solid" && "bg-elevated hover:bg-elevated-2",
        active && "bg-elevated text-fg",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});
